import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Edit, Trash2, Image as ImageIcon, Play, Star, Upload, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ['Events', 'Team', 'Awards', 'Community', 'Fun'];

const emptyForm = {
  title: '',
  caption: '',
  media_type: 'photo',
  media_url: '',
  thumbnail_url: '',
  category: 'Events',
  is_featured: false,
  order: 99,
};

const AdminSocialActivities = () => {
  const { getAuthHeader } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const { data } = await axios.get(`${API}/social-activities`);
      setItems(data);
    } catch { toast.error('Failed to load activities'); }
    finally { setLoading(false); }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const openModal = (item = null) => {
    setEditing(item);
    setForm(item ? { ...emptyForm, ...item } : emptyForm);
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.media_url) { toast.error('Please upload a photo or video first'); return; }
    try {
      if (editing) {
        await axios.put(`${API}/social-activities/${editing.id}`, form, { headers: getAuthHeader() });
        toast.success('Activity updated');
      } else {
        await axios.post(`${API}/social-activities`, form, { headers: getAuthHeader() });
        toast.success('Activity added');
      }
      setIsModalOpen(false);
      fetchItems();
    } catch { toast.error('Failed to save activity'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this activity?')) return;
    try {
      await axios.delete(`${API}/social-activities/${id}`, { headers: getAuthHeader() });
      toast.success('Deleted');
      fetchItems();
    } catch { toast.error('Failed to delete'); }
  };

  // ── Upload handler ─────────────────────────────────────────────────────────
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isVideo = file.type.startsWith('video/');
    const isPhoto = file.type.startsWith('image/');
    if (!isPhoto && !isVideo) { toast.error('Only image or video files allowed'); return; }
    if (file.size > 100 * 1024 * 1024) { toast.error('File too large (max 100 MB)'); return; }

    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);

    try {
      const endpoint = isVideo ? `${API}/upload-video` : `${API}/upload-image`;
      const { data } = await axios.post(endpoint, fd, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
      setForm(prev => ({
        ...prev,
        media_url: data.url,
        media_type: isVideo ? 'video' : 'photo',
      }));
      toast.success('Uploaded successfully');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const mediaCount = { photo: items.filter(i => i.media_type === 'photo').length, video: items.filter(i => i.media_type === 'video').length };

  return (
    <div className="p-6 sm:p-8" data-testid="admin-social-activities">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-playfair font-bold text-stone-900">Social Activities</h1>
          <p className="text-stone-500 text-sm mt-1">{mediaCount.photo} photos · {mediaCount.video} videos</p>
        </div>
        <Button onClick={() => openModal()} data-testid="add-activity-btn" className="bg-emerald-900 hover:bg-emerald-800 text-white">
          <Plus className="w-4 h-4 mr-2" /> Add Activity
        </Button>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <div key={i} className="aspect-square bg-stone-200 rounded-xl animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 border-2 border-dashed border-stone-300 rounded-xl">
          <ImageIcon className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500 font-medium">No activities yet</p>
          <p className="text-stone-400 text-sm">Click "Add Activity" to upload photos or videos</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map(item => (
            <div key={item.id} className="group relative bg-stone-100 rounded-xl overflow-hidden" data-testid={`admin-activity-${item.id}`}>
              {/* Media preview */}
              {item.media_type === 'photo' ? (
                <div className="aspect-square overflow-hidden">
                  <img src={item.media_url} alt={item.title} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="aspect-square relative bg-stone-900">
                  <video src={item.media_url} className="w-full h-full object-cover" preload="metadata" muted />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full bg-white/30 flex items-center justify-center">
                      <Play className="w-5 h-5 text-white fill-white ml-0.5" />
                    </div>
                  </div>
                </div>
              )}

              {/* Badges */}
              <div className="absolute top-2 left-2 flex gap-1 flex-wrap">
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold text-white ${item.media_type === 'photo' ? 'bg-emerald-700' : 'bg-blue-600'}`}>
                  {item.media_type === 'photo' ? 'PHOTO' : 'VIDEO'}
                </span>
                {item.is_featured && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-amber-500 text-white flex items-center gap-0.5">
                    <Star className="w-2.5 h-2.5 fill-white" />
                  </span>
                )}
              </div>

              {/* Hover actions */}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                <button onClick={() => openModal(item)} className="p-2 bg-white rounded-lg text-stone-800 hover:bg-stone-100 transition-colors">
                  <Edit className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(item.id)} className="p-2 bg-red-500 rounded-lg text-white hover:bg-red-600 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {/* Caption */}
              {(item.title || item.caption) && (
                <div className="p-2.5 bg-white border-t border-stone-100">
                  {item.title && <p className="text-xs font-bold text-stone-800 truncate">{item.title}</p>}
                  {item.caption && <p className="text-[10px] text-stone-500 truncate">{item.caption}</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-playfair">{editing ? 'Edit Activity' : 'Add Social Activity'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            {/* Upload zone */}
            <div className="border-2 border-dashed border-stone-300 rounded-xl p-5 text-center hover:border-emerald-700 transition-colors">
              {form.media_url ? (
                <div className="relative">
                  {form.media_type === 'photo' ? (
                    <img src={form.media_url} alt="Preview" className="w-full h-40 object-cover rounded-lg mb-2" />
                  ) : (
                    <video src={form.media_url} className="w-full h-40 object-cover rounded-lg mb-2" controls />
                  )}
                  <button
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, media_url: '', media_type: 'photo' }))}
                    className="absolute top-2 right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white"
                  >
                    <X className="w-3 h-3" />
                  </button>
                  <p className="text-xs text-stone-500">Click × to replace</p>
                </div>
              ) : (
                <label htmlFor="sa-upload" className="cursor-pointer flex flex-col items-center gap-2">
                  {uploading ? (
                    <Loader2 className="w-8 h-8 text-emerald-700 animate-spin" />
                  ) : (
                    <Upload className="w-8 h-8 text-stone-400" />
                  )}
                  <div>
                    <p className="font-semibold text-stone-800 text-sm">
                      {uploading ? 'Uploading…' : 'Click to upload photo or video'}
                    </p>
                    <p className="text-xs text-stone-400 mt-0.5">JPG, PNG, MP4, MOV (max 100 MB)</p>
                  </div>
                  <input
                    id="sa-upload"
                    type="file"
                    accept="image/*,video/*"
                    onChange={handleUpload}
                    disabled={uploading}
                    className="hidden"
                    data-testid="sa-file-input"
                  />
                </label>
              )}
            </div>

            <div>
              <label className="text-xs font-semibold text-stone-600 uppercase tracking-wide mb-1 block">Title (optional)</label>
              <Input name="title" value={form.title} onChange={handleChange} placeholder="e.g. Annual Day Celebration" />
            </div>

            <div>
              <label className="text-xs font-semibold text-stone-600 uppercase tracking-wide mb-1 block">Caption</label>
              <Textarea name="caption" value={form.caption} onChange={handleChange} rows={3} placeholder="Describe this activity…" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-stone-600 uppercase tracking-wide mb-1 block">Category</label>
                <select name="category" value={form.category} onChange={handleChange}
                  className="w-full px-3 py-2 text-sm border border-stone-300 rounded-md outline-none focus:ring-2 focus:ring-emerald-600 bg-white">
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-stone-600 uppercase tracking-wide mb-1 block">Order</label>
                <Input type="number" name="order" value={form.order} onChange={handleChange} min="1" />
              </div>
            </div>

            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" name="is_featured" checked={form.is_featured} onChange={handleChange} className="w-4 h-4 accent-amber-600" />
              <span className="text-sm text-stone-700 font-medium flex items-center gap-1.5">
                <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" /> Mark as Featured
              </span>
            </label>

            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={uploading} className="flex-1 bg-emerald-900 hover:bg-emerald-800 text-white">
                {editing ? 'Update Activity' : 'Add Activity'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSocialActivities;
